import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Alert,
  TouchableOpacity,
  Platform,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import { Button, Text, Surface } from 'react-native-paper';
import HeaderBar from '../components/HeaderBar';
import MaxWidthContainer from '../components/MaxWidthContainer';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types/navigation';
import SlidingMenu from '../components/SlidingMenu';
import authService from '../services/authService';
import { brandPrimary, brandPrimarySoft } from '../theme/paperTheme';

/** Turn on when backend supports helper coins, payouts, or posting history. */
const SHOW_PROFILE_EXTRAS = false;

type StoredUser = {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  name?: string;
  email?: string;
};

function displayNameFromUser(u: StoredUser | null): string {
  if (!u) return 'Member';
  const first = (u.firstName ?? '').trim();
  const last = (u.lastName ?? '').trim();
  const combined = `${first} ${last}`.trim();
  if (combined.length > 0) return combined;
  const dn = (u.displayName ?? '').trim();
  if (dn.length > 0) return dn;
  const n = (u.name ?? '').trim();
  if (n.length > 0) return n;
  return 'Member';
}

/** Alert/confirm on web — RN Alert.alert is a no-op in many web builds. */
function webConfirm(message: string): boolean {
  const g = globalThis as typeof globalThis & { confirm?: (msg?: string) => boolean };
  return g.confirm?.(message) ?? false;
}

function webAlert(message: string): void {
  const g = globalThis as typeof globalThis & { alert?: (msg?: string) => void };
  g.alert?.(message);
}

const ProfileSettingsScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [user, setUser] = useState<{ name: string; email: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const toggleMenu = () => setIsMenuVisible((v) => !v);

  const loadUser = useCallback(async () => {
    const currentUser = (await authService.getCurrentUser()) as StoredUser | null;
    setUser({
      name: displayNameFromUser(currentUser),
      email: (currentUser?.email ?? '').trim() || '—',
    });
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const performDeleteAccount = async () => {
    setDeleting(true);
    try {
      await authService.deleteAccount();
      await authService.logout();
      navigation.reset({ index: 0, routes: [{ name: 'Login' }] });
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { error?: string; message?: string } };
        message?: string;
      };
      const message =
        ax.response?.data?.error ||
        ax.response?.data?.message ||
        ax.message ||
        'Could not delete your account. Please try again.';
      if (Platform.OS === 'web') {
        webAlert(message);
      } else {
        Alert.alert('Something went wrong', message);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteAccount = () => {
    const message =
      'This permanently removes your account and cannot be undone.';

    if (Platform.OS === 'web') {
      if (webConfirm(message)) {
        performDeleteAccount();
      }
      return;
    }

    Alert.alert('Delete account', message, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: performDeleteAccount },
    ]);
  };

  return (
    <View style={styles.container}>
      <Surface style={styles.surface}>
        <View style={styles.headerWrap}>
          <HeaderBar
            rightContent={
              <TouchableOpacity onPress={toggleMenu} style={styles.menuButton}>
                <Text style={styles.menuText}>☰</Text>
              </TouchableOpacity>
            }
            onLogoPress={() => navigation.navigate('Home')}
            style={styles.headerBar}
          />
        </View>

        <MaxWidthContainer style={isDesktop ? styles.wideContainer : undefined}>
          <ScrollView
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
          >
            <Text style={styles.screenLabel}>Your account</Text>
            <Text style={styles.screenTitle}>Profile settings</Text>

            <Surface style={styles.profileCard} elevation={0}>
              <View style={styles.avatar}>
                <Text style={styles.avatarInitial}>
                  {user?.name?.[0]?.toUpperCase() || 'U'}
                </Text>
              </View>
              <Text style={styles.profileName}>{user?.name ?? '—'}</Text>
              <Text style={styles.profileEmail}>{user?.email ?? '—'}</Text>
            </Surface>

            {SHOW_PROFILE_EXTRAS ? (
              <Surface style={styles.infoCard} elevation={0}>
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Helper coins</Text>
                  <Text style={styles.infoValue}>—</Text>
                </View>
                <View style={styles.divider} />
                <Text style={styles.infoSectionTitle}>Available rewards</Text>
                <View style={styles.divider} />
                <TouchableOpacity style={styles.infoRow} activeOpacity={0.7}>
                  <Text style={styles.infoLink}>Payments & payouts</Text>
                </TouchableOpacity>
                <View style={styles.divider} />
                <TouchableOpacity style={styles.infoRow} activeOpacity={0.7}>
                  <Text style={styles.infoLink}>Posting history</Text>
                </TouchableOpacity>
              </Surface>
            ) : null}

            <Surface style={styles.dangerCard} elevation={0}>
              <Text style={styles.dangerTitle}>Delete account</Text>
              <Text style={styles.dangerText}>
                Permanently remove your account and all associated data. This action cannot be
                undone.
              </Text>
              <Button
                mode="contained"
                onPress={handleDeleteAccount}
                loading={deleting}
                disabled={deleting}
                style={styles.deleteButton}
                buttonColor="#d32f2f"
                textColor="#ffffff"
              >
                Delete account
              </Button>
            </Surface>
          </ScrollView>
        </MaxWidthContainer>
      </Surface>
      <SlidingMenu isVisible={isMenuVisible} onClose={toggleMenu} />
    </View>
  );
};

const cardShadow = Platform.OS === 'web'
  ? { boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)' }
  : {
      shadowColor: '#0F172A',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.06,
      shadowRadius: 12,
      elevation: 3,
    };

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  wideContainer: {
    maxWidth: 960,
  },
  headerWrap: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)' }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  headerBar: {
    marginHorizontal: 8,
    marginBottom: 0,
  },
  menuButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuText: {
    fontSize: 36,
    color: '#1E293B',
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 48,
  },
  screenLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 8,
  },
  screenTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1E293B',
    letterSpacing: -0.5,
    marginBottom: 24,
    lineHeight: 36,
  },
  profileCard: {
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 28,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...cardShadow,
  },
  avatar: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: brandPrimarySoft,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  avatarInitial: {
    fontSize: 40,
    color: brandPrimary,
    fontWeight: '700',
  },
  profileName: {
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 6,
    color: '#1E293B',
    textAlign: 'center',
  },
  profileEmail: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
  },
  infoCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...cardShadow,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  infoLabel: {
    fontSize: 16,
    color: '#475569',
    fontWeight: '500',
  },
  infoValue: {
    fontSize: 16,
    color: brandPrimary,
    fontWeight: '700',
  },
  infoSectionTitle: {
    fontSize: 15,
    color: '#1E293B',
    fontWeight: '600',
    paddingVertical: 8,
  },
  infoLink: {
    fontSize: 16,
    color: '#1E293B',
    fontWeight: '500',
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.06)',
  },
  dangerCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(211, 47, 47, 0.2)',
    ...cardShadow,
  },
  dangerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
  },
  dangerText: {
    fontSize: 15,
    color: '#64748B',
    lineHeight: 22,
    marginBottom: 20,
  },
  deleteButton: {
    borderRadius: 999,
    paddingVertical: 2,
  },
});

export default ProfileSettingsScreen;
